import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { HiOutlineExclamationCircle, HiPlus } from "react-icons/hi";
import MeritoButton from "../common/MeritoButtons/MeritoButtons";
import ConfirmDialog from "../common/ConfirmDialog";
import CircularLoader from "../common/CircularLoader/CircularLoader";
import { BankAccountCard } from "./BankAccountCard";
import { UnidentifiedAccountCard } from "./UnidentifiedAccountCard";
import { useBankStatements } from "./BankStatementsContext";
import { DocumentList } from "./DocumentList";
import { FileUploadQueue } from "./FileUploadQueue";
import { BankStatementUploadItem } from "./types";
import {
  getRequiredStatementPeriod,
  DIALOG_MODAL_STYLES,
  isDocumentFailed,
  isDocumentPasswordRequired,
  isDocumentSubmittable,
  isPulseBankAccountActionable,
} from "./utils";
import { getUnsubmittedDocIds } from "./selectors";
import styles from "./BankStatements.module.scss";

const IDENTIFICATION_PROGRESS_TITLES = [
  "Reading your statements",
  "Finding the banks",
  "Identifying your accounts",
  "Grouping statements",
  "Getting your accounts ready",
] as const;

const IDENTIFICATION_PROGRESS_INTERVAL_MS = 20_000;
const IDENTIFICATION_COMPLETION_INTERVAL_MS = 2_000;

const StatementGuidance = () => {
  const period = getRequiredStatementPeriod();
  return (
    <div className={styles.guidance}>
      PDF statements for every bank account used by your business, including
      Current, CC, OD and Share Capital accounts, from{" "}
      <strong>{period.formattedText}.</strong>
    </div>
  );
};

const ReadingStatements = ({
  title = IDENTIFICATION_PROGRESS_TITLES[0],
}: {
  title?: string;
}) => (
  <div className={styles.readingState}>
    <CircularLoader />
    <strong>{title}</strong>
    <span>This usually takes about a minute.</span>
  </div>
);

const IdentificationProgress = ({
  identificationComplete,
  onFinished,
}: {
  identificationComplete: boolean;
  onFinished: () => void;
}) => {
  const [titleIndex, setTitleIndex] = useState(0);
  const lastTitleIndex = IDENTIFICATION_PROGRESS_TITLES.length - 1;

  useEffect(() => {
    if (identificationComplete || titleIndex >= lastTitleIndex) return;

    const timer = window.setTimeout(() => {
      setTitleIndex((current) => Math.min(current + 1, lastTitleIndex));
    }, IDENTIFICATION_PROGRESS_INTERVAL_MS);

    return () => window.clearTimeout(timer);
  }, [identificationComplete, lastTitleIndex, titleIndex]);

  useEffect(() => {
    if (!identificationComplete) return;

    const timer = window.setTimeout(() => {
      if (titleIndex < lastTitleIndex) {
        setTitleIndex(titleIndex + 1);
        return;
      }
      onFinished();
    }, IDENTIFICATION_COMPLETION_INTERVAL_MS);

    return () => window.clearTimeout(timer);
  }, [identificationComplete, lastTitleIndex, onFinished, titleIndex]);

  return <ReadingStatements title={IDENTIFICATION_PROGRESS_TITLES[titleIndex]} />;
};

const FirstTimeUpload = () => {
  const {
    state,
    submitAnalysis,
    isFirstTimeReview,
    hasLocalWork,
    removeDocument,
    submitDocumentPassword,
  } = useBankStatements();
  const [items, setItems] = useState<BankStatementUploadItem[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const mountedRef = useRef(true);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const localDocIds = new Set(
    items.map((item) => item.doc?.id).filter((id): id is number => !!id)
  );
  const restoredDocuments = state.pendingIdentification.filter(
    (doc) => !localDocIds.has(doc.id)
  );
  const allDocIds = Array.from(
    new Set([
      ...restoredDocuments
        .filter(isDocumentSubmittable)
        .map((doc) => doc.id),
      ...Array.from(localDocIds),
    ])
  );
  const hasReviewRows = !!items.length || !!restoredDocuments.length;
  const queueReady = items.every((item) => item.status === "uploaded");
  const pristineFirstTime = !hasReviewRows && !isFirstTimeReview;
  const firstTimeVariant = pristineFirstTime ? "landing" : "review";
  const statementCount = items.length + restoredDocuments.length;
  const attentionCount =
    items.filter(
      (item) =>
        item.status === "failed" || item.status === "password_required"
    ).length +
    restoredDocuments.filter(
      (doc) => isDocumentFailed(doc) || isDocumentPasswordRequired(doc)
    ).length;

  const hasAttentionItems = attentionCount > 0;

  const handleContinue = async () => {
    if (!allDocIds.length || !queueReady || hasAttentionItems) return;
    setSubmitting(true);
    try {
      await submitAnalysis(allDocIds);
    } catch (_error) {
    } finally {
      // A successful submission unmounts this screen, so only settle the
      // button while we are still on it.
      if (mountedRef.current) setSubmitting(false);
    }
  };

  return (
    <div
      className={`${styles.moduleRoot} ${
        pristineFirstTime ? styles.firstTimeLandingRoot : styles.firstTimeReviewRoot
      }`}
    >
      <div className={styles.firstTimeIntro}>
        <h1>Upload your bank statements</h1>
        <StatementGuidance />
      </div>
      <FileUploadQueue
        scope="first-time-upload"
        compact={hasReviewRows || isFirstTimeReview}
        firstTimeVariant={firstTimeVariant}
        listHeader={
          hasReviewRows ? (
            <div className={styles.firstTimeStatementsHeader}>
              <strong>Statements added ({statementCount})</strong>
              {attentionCount ? (
                <span className={styles.attentionChip}>
                  <HiOutlineExclamationCircle />
                  {attentionCount} {attentionCount === 1 ? "file" : "files"}{" "}
                  needs attention
                </span>
              ) : null}
            </div>
          ) : null
        }
        reviewListChildren={
          restoredDocuments.length ? (
            <DocumentList
              documents={restoredDocuments}
              onRemove={(doc) => removeDocument(doc.id, true)}
              onSubmitPassword={async (doc, password, signal) => {
                await submitDocumentPassword(doc, password, { signal });
              }}
            />
          ) : null
        }
        onItemsChange={setItems}
      />
      {hasReviewRows ? (
        <div className={styles.continueRow}>
          <MeritoButton
            type="primary-1"
            size="md2"
            className={styles.firstTimeContinueButton}
            btnText="Continue"
            loading={submitting}
            disabled={
              !allDocIds.length || !queueReady || hasAttentionItems || submitting
            }
            onClick={handleContinue}
          />
        </div>
      ) : null}
      {state.submissionError ? (
        <div className={styles.rootError}>{state.submissionError}</div>
      ) : null}
    </div>
  );
};

const AddBankAccountDialog = ({
  visible,
  onClose,
}: {
  visible: boolean;
  onClose: () => void;
}) => {
  const { submitAnalysis, stageUploadedDocument } = useBankStatements();
  const [items, setItems] = useState<BankStatementUploadItem[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [dialogKey, setDialogKey] = useState(0);
  const docIds = items
    .filter((item) => item.status === "uploaded" && item.doc?.id)
    .map((item) => item.doc!.id);
  const ready = !!items.length && items.every((item) => item.status === "uploaded");

  const closeAndReset = () => {
    setItems([]);
    setDialogKey((current) => current + 1);
    onClose();
  };

  const handleContinue = async () => {
    if (!ready || !docIds.length) return;
    setSubmitting(true);
    try {
      items.forEach((item) => {
        if (!item.doc) return;
        stageUploadedDocument(item.doc);
      });
      await submitAnalysis(docIds);
      closeAndReset();
    } catch (_error) {
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConfirmDialog
      visible={visible}
      toggle={closeAndReset}
      showButton={false}
      showCloseBtn={false}
      modalStyles={DIALOG_MODAL_STYLES}
      contentClassName={styles.addAccountDialogContentBox}
      bodyClassName={styles.addAccountDialogModalBody}
      size="md"
    >
      <div className={styles.addAccountDialog}>
        <div className={styles.addAccountDialogContent}>
          <div className={styles.addAccountDialogHeader}>
            <h2>Add new bank account</h2>
            <div>
              <p>Upload PDF statements for one bank account.</p>
              <p>
                From{" "}
                <strong className={styles.addAccountDialogDate}>
                  {getRequiredStatementPeriod().formattedText.replace(
                    " to ",
                    " - "
                  )}
                  .
                </strong>
              </p>
            </div>
          </div>
          <FileUploadQueue
            key={dialogKey}
            scope="add-bank-account"
            compact={!!items.length}
            dialogVariant
            stageImmediately={false}
            onItemsChange={setItems}
          />
        </div>
        <div className={styles.dialogActions}>
          <MeritoButton
            type="secondary-2"
            size="lg"
            btnText="Close"
            isRounded
            onClick={closeAndReset}
          />
          <MeritoButton
            type="primary-1"
            size="lg"
            btnText="Continue"
            isRounded
            loading={submitting}
            disabled={!ready || submitting}
            onClick={handleContinue}
          />
        </div>
      </div>
    </ConfirmDialog>
  );
};

export const BankStatementsModule = () => {
  const {
    state,
    isFirstTime,
    isReadingStatements,
    refresh,
    submitAnalysis,
    module,
  } = useBankStatements();
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const [retryingSubmission, setRetryingSubmission] = useState(false);
  const [identificationProgressActive, setIdentificationProgressActive] =
    useState(false);

  useLayoutEffect(() => {
    if (isReadingStatements) setIdentificationProgressActive(true);
  }, [isReadingStatements]);

  const finishIdentificationProgress = useCallback(() => {
    setIdentificationProgressActive(false);
  }, []);

  const sortedAccounts = useMemo(
    () =>
      state.accounts
        .map((account, index) => ({ account, index }))
        .sort((a, b) => {
          if (module !== "pulse") return a.account.id - b.account.id;

          const aRequiresAction = isPulseBankAccountActionable(a.account);
          const bRequiresAction = isPulseBankAccountActionable(b.account);

          if (aRequiresAction !== bRequiresAction) {
            return aRequiresAction ? -1 : 1;
          }
          if (aRequiresAction && bRequiresAction) {
            return (b.account.dueBy || 0) - (a.account.dueBy || 0);
          }
          return a.index - b.index;
        })
        .map(({ account }) => account),
    [module, state.accounts]
  );
  const retryDocIds = useMemo(
    () => getUnsubmittedDocIds(state),
    [state]
  );

  const retrySubmission = async () => {
    if (!retryDocIds.length) return;
    setRetryingSubmission(true);
    try {
      await submitAnalysis(retryDocIds);
    } catch (_error) {
    } finally {
      setRetryingSubmission(false);
    }
  };

  if (state.loading && !state.loaded) {
    return (
      <div className={styles.moduleRoot}>
        <ReadingStatements />
      </div>
    );
  }

  if (state.loadError && !state.accounts.length && !state.pendingIdentification.length) {
    return (
      <div className={styles.moduleRoot}>
        <div className={styles.loadError}>
          <strong>We couldn’t load your bank statements.</strong>
          <span>{state.loadError}</span>
          <MeritoButton
            type="secondary-1"
            size="md2"
            btnText="Try again"
            onClick={refresh}
          />
        </div>
      </div>
    );
  }

  if (isReadingStatements || identificationProgressActive) {
    return (
      <div className={styles.moduleRoot}>
        <IdentificationProgress
          identificationComplete={
            identificationProgressActive && !isReadingStatements
          }
          onFinished={finishIdentificationProgress}
        />
      </div>
    );
  }

  if (isFirstTime) return <FirstTimeUpload />;

  return (
    <div className={styles.moduleRoot}>
      <h1>Bank Statements</h1>
      <p className={styles.subtitle}>
        Upload PDF statements for every bank account (Current, CC, OD, Share
        Capital) used by your business in the last 12 months.
      </p>
      <div className={styles.eyebrow}>
        YOUR ACCOUNTS ({state.accounts.length})
      </div>
      <div className={styles.accountList}>
        {sortedAccounts.map((account) => (
          <BankAccountCard
            key={account.id}
            account={account}
            companyId={state.companyId}
          />
        ))}
        {state.pendingIdentification.length ? (
          <UnidentifiedAccountCard
            documents={state.pendingIdentification}
            companyId={state.companyId}
          />
        ) : null}
      </div>
      <div className={styles.addAccountRow}>
        <MeritoButton
          type="secondary-1"
          size="md2"
          icon={<HiPlus />}
          btnText="Add Bank Account"
          onClick={() => setAddAccountOpen(true)}
        />
      </div>
      {state.submissionError ? (
        <div className={styles.rootError}>
          <span>{state.submissionError}</span>
          {retryDocIds.length ? (
            <MeritoButton
              type="secondary-1"
              size="md2"
              btnText="Retry"
              loading={retryingSubmission}
              onClick={retrySubmission}
            />
          ) : null}
        </div>
      ) : null}
      <AddBankAccountDialog
        visible={addAccountOpen}
        onClose={() => setAddAccountOpen(false)}
      />
    </div>
  );
};

export default BankStatementsModule;
